// Modules
import React, { ComponentType, useEffect, useMemo } from 'react'
import { Route, RouteProps as ReactDOMRouterProps, useLocation } from 'react-router-dom'

// Application
import { useRedirectTo } from 'hooks/useRedirectTo'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import { RedirectSingleSpa } from 'components'
import { ROUTES_PATH } from 'constants/routesPath'
import { KEEP_LOGGED_LS_KEY } from 'constants/app'
import { userPathDetect } from 'utils'

interface RouteProps extends ReactDOMRouterProps {
    restricted?: boolean
    component: React.ComponentType
    template?: ComponentType<ReactDOMRouterProps>
}

export const Router: React.FC<RouteProps> = ({ restricted = false, component: Component, template: Template, ...rest }) => {
    const location = useLocation()
    const { getConfigByName, isFeatureEnabled } = useRemoteConfig()
    const newRedirectFlow = isFeatureEnabled('AUTH_NEW_REDIRECT_FLOW')
    const { redirectTo } = useRedirectTo()
    const storage = localStorage.getItem('@Livia')!
    const token = JSON.parse(storage)?.token
    const limitedFlow = JSON.parse(storage)?.limitedFlow
    const keepLoggedLs = JSON.parse(localStorage.getItem(KEEP_LOGGED_LS_KEY)!)
    const { isLoginPage } = userPathDetect()

    useEffect(() => {
        if (!newRedirectFlow) {
            if (redirectTo) {
                sessionStorage.setItem('@LiviaAuth:Campaigns', redirectTo)
            } else if (location.pathname === '/login') {
                sessionStorage.removeItem('@LiviaAuth:Campaigns')
            }
        }
    }, [location.pathname, newRedirectFlow, redirectTo])

    const redirectPathHome = (getConfigByName('AUTH_REDIRECT_LOGIN') as string) || defaultConfig.strings.AUTH_REDIRECT_LOGIN

    const isExternalURL = redirectTo?.startsWith('/http') || redirectTo?.startsWith('http')
    const redirectPathLogin = redirectTo
    let redirectLink = `${ROUTES_PATH.FORWARDING_APP}${location.search}`

    if (!newRedirectFlow) {
        redirectLink = redirectPathLogin ? redirectPathLogin : redirectPathHome
    }

    const isKeepLogged = useMemo(() => !!keepLoggedLs?.token, [keepLoggedLs])

    const shouldRedirect = useMemo(() => token && restricted && !limitedFlow && !isExternalURL && !isKeepLogged && !isLoginPage, [
        token,
        restricted,
        limitedFlow,
        isExternalURL,
        isKeepLogged,
        isLoginPage,
    ])

    const renderContent = () => {
        if (shouldRedirect) {
            return <RedirectSingleSpa to={redirectLink} />
        }

        if (Template) {
            return (
                <Template>
                    <Component />
                </Template>
            )
        }

        return <Component />
    }

    return <Route {...rest} render={renderContent} />
}
